import React, { useEffect, useState, useCallback } from "react";
import { X, RotateCw, Download, ExternalLink } from "lucide-react";

/**
 * Fullscreen preview lightbox for submission attachments.
 *
 * Supports single- and multi-attachment submissions. Callers pass an
 * ``items`` array of ``{url, filename, kind}`` and an ``initialIndex``;
 * the lightbox maintains its own current-index state so keyboard
 * navigation (← / →) and the on-screen prev/next arrows all work
 * without the parent re-rendering on every step.
 *
 * Keyboard:
 *   - Esc closes
 *   - R rotates the current image (no-op on PDFs)
 *   - ← / → navigates when items.length > 1
 */
export default function AttachmentLightbox({
  open,
  onClose,
  items = [],
  initialIndex = 0,
}) {
  const [index, setIndex] = useState(initialIndex);
  const [rotation, setRotation] = useState(0);

  const total = items.length;
  const current = items[index];

  const goPrev = useCallback(() => {
    if (total < 2) return;
    setIndex((i) => (i - 1 + total) % total);
  }, [total]);
  const goNext = useCallback(() => {
    if (total < 2) return;
    setIndex((i) => (i + 1) % total);
  }, [total]);

  const rotate = useCallback(() => setRotation((r) => (r + 90) % 360), []);

  const handleKey = useCallback((e) => {
    if (!open) return;
    if (e.key === "Escape") onClose?.();
    if (e.key === "r" || e.key === "R") { if (current?.kind === "image") rotate(); }
    if (e.key === "ArrowRight") goNext();
    if (e.key === "ArrowLeft") goPrev();
  }, [open, onClose, current, rotate, goNext, goPrev]);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener("keydown", handleKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKey);
      document.body.style.overflow = prev;
    };
  }, [open, handleKey]);

  // Reset rotation whenever the current file changes; snap the initial
  // index when the caller changes it (e.g. clicked thumbnail 3).
  useEffect(() => { setRotation(0); }, [current?.url]);
  useEffect(() => { setIndex(initialIndex); }, [initialIndex, open]);

  if (!open || !current) return null;

  const { url, filename, kind } = current;

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/95 flex flex-col"
      role="dialog"
      aria-modal="true"
      data-testid="attachment-lightbox"
      onClick={onClose}
    >
      {/* Toolbar */}
      <div
        className="flex items-center justify-between px-4 py-3 text-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-sm font-medium truncate max-w-[60%] flex items-center gap-2">
          <span title={filename}>{filename || "attachment"}</span>
          {total > 1 && (
            <span
              data-testid="attachment-lightbox-counter"
              className="text-xs text-slate-400 shrink-0"
            >
              {index + 1} / {total}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {kind === "image" && (
            <button
              type="button"
              onClick={rotate}
              data-testid="attachment-lightbox-rotate"
              className="p-2 rounded hover:bg-slate-800 transition-colors"
              title="Rotate 90° (R)"
              aria-label="Rotate"
            >
              <RotateCw className="h-4 w-4" />
            </button>
          )}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="attachment-lightbox-download"
            className="p-2 rounded hover:bg-slate-800 transition-colors"
            title="Download"
            aria-label="Download"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="h-4 w-4" />
          </a>
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded hover:bg-slate-800 transition-colors"
            title="Open in new tab"
            aria-label="Open in new tab"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
          <button
            type="button"
            onClick={onClose}
            data-testid="attachment-lightbox-close"
            className="p-2 rounded hover:bg-slate-800 transition-colors ml-1"
            title="Close (Esc)"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div
        className="flex-1 flex items-center justify-center overflow-hidden px-4 pb-4"
        onClick={(e) => e.stopPropagation()}
      >
        {kind === "image" ? (
          <img
            src={url}
            alt={filename || "attachment"}
            data-testid="attachment-lightbox-image"
            style={{
              transform: `rotate(${rotation}deg)`,
              maxHeight: rotation % 180 === 0 ? "calc(100vh - 96px)" : "calc(100vw - 96px)",
              maxWidth: rotation % 180 === 0 ? "calc(100vw - 96px)" : "calc(100vh - 96px)",
              transition: "transform 200ms ease",
            }}
            className="object-contain shadow-2xl bg-white"
          />
        ) : (
          <iframe
            src={url}
            title={filename || "attachment"}
            data-testid="attachment-lightbox-pdf"
            className="w-full h-full bg-white rounded"
          />
        )}
      </div>

      {/* Nav arrows (multi-item only) */}
      {total > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-slate-800/80 text-slate-100 hover:bg-slate-700 text-2xl leading-none"
            aria-label="Previous"
            data-testid="attachment-lightbox-prev"
          >
            ‹
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); goNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-slate-800/80 text-slate-100 hover:bg-slate-700 text-2xl leading-none"
            aria-label="Next"
            data-testid="attachment-lightbox-next"
          >
            ›
          </button>
        </>
      )}
    </div>
  );
}
